import { Editor } from '@tiptap/core'
import { MarkType } from '@tiptap/pm/model'
import { Transaction } from '@tiptap/pm/state'
import {
  SUGGESTION_MARK_NAME,
  SUGGESTION_TYPE_DELETION,
  SUGGESTION_TYPE_INSERTION,
  SuggestionBaseAttrs,
} from '../../../../../../backend/src/tiptap/editor/extensions/marks/suggestion'
import { createBaseAttrs, TRACK_CHANGES_SKIP_META, trackChangesPluginKey, TrackChangesUser } from './track-changes-plugin'

export type FindReplaceTrackingOptions = {
  getEnabled: () => boolean
  getCurrentUser: () => TrackChangesUser | null
}

type FindResult = { from: number; to: number }

// Marks the old text as a deletion suggestion and the replacement as its paired insertion suggestion,
// instead of editing the document directly — the old text stays (struck through) until reviewed.
function markMatchAsTrackedReplacement(
  editor: Editor,
  transaction: Transaction,
  suggestionMarkType: MarkType,
  match: FindResult,
  replaceTerm: string,
  baseAttrs: SuggestionBaseAttrs
) {
  transaction.addMark(match.from, match.to, suggestionMarkType.create({ ...baseAttrs, type: SUGGESTION_TYPE_DELETION }))
  if (replaceTerm.length > 0) {
    transaction.insert(match.to, editor.state.schema.text(replaceTerm))
    transaction.addMark(match.to, match.to + replaceTerm.length, suggestionMarkType.create({ ...baseAttrs, type: SUGGESTION_TYPE_INSERTION }))
  }
}

export function runReplaceWithTracking(editor: Editor, options: FindReplaceTrackingOptions): boolean {
  const currentUser = options.getCurrentUser()
  if (!options.getEnabled() || currentUser === null) {
    return editor.commands.replace()
  }

  const { results, currentIndex, replaceTerm } = editor.storage.findAndReplace
  const match = currentIndex !== null ? (results[currentIndex] ?? null) : null
  if (match === null) {
    return false
  }

  const suggestionMarkType = editor.state.schema.marks[SUGGESTION_MARK_NAME] ?? null
  if (suggestionMarkType === null) {
    return false
  }

  const transaction = editor.state.tr
  markMatchAsTrackedReplacement(editor, transaction, suggestionMarkType, match, replaceTerm, createBaseAttrs(currentUser))
  transaction.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
  editor.view.dispatch(transaction)

  // The old text is still there and still matches, so this re-reads the recomputed result list to
  // skip past it to the next untouched occurrence.
  editor.commands.goToNextResult()
  return true
}

export function runReplaceAllWithTracking(editor: Editor, options: FindReplaceTrackingOptions): boolean {
  const currentUser = options.getCurrentUser()
  if (!options.getEnabled() || currentUser === null) {
    return editor.commands.replaceAll()
  }

  const { results, replaceTerm } = editor.storage.findAndReplace
  if (results.length === 0) {
    return false
  }

  const suggestionMarkType = editor.state.schema.marks[SUGGESTION_MARK_NAME] ?? null
  if (suggestionMarkType === null) {
    return false
  }

  const transaction = editor.state.tr
  // Highest position first, so replacing a later match never shifts positions still to be processed.
  const orderedResults = [...results].sort((first, second) => second.from - first.from)
  for (const match of orderedResults) {
    // Fresh attrs per match — each occurrence gets its own suggestion id.
    markMatchAsTrackedReplacement(editor, transaction, suggestionMarkType, match, replaceTerm, createBaseAttrs(currentUser))
  }
  transaction.setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
  editor.view.dispatch(transaction)

  // Replaced text is still present as pending suggestions and would still match, so close the search
  // out rather than show a stale count.
  editor.commands.clearSearch()
  return true
}
